import { REALTIME_SAMPLE_RATE } from '@integrate/contracts';

/*
  Raw samples, in both directions.

  WHY NONE OF `VoiceCapture` IS REUSED, and it is a constraint rather than a preference. Dictation
  records through `MediaRecorder` into a compressed container — Opus in WebM, usually — because that
  is what the transcription endpoint reads and because it is forty times smaller than the samples.
  The realtime surface reads and writes RAW PCM16 at 24 kHz, and there is no transcoder in this
  product: `tools/license-check.cjs` blocks ffmpeg (LGPL-2.1+), which is exactly why the container is
  chosen in the browser in the first place. So a compressed blob cannot become PCM here, and this is
  a second capture path rather than a wrapper around the first.

  What IS shared is the permission: `getUserMedia({ audio: true })` asks once and the answer is the
  browser's, not this file's.

  THE ARITHMETIC IS SEPARATED FROM THE BROWSER ON PURPOSE. happy-dom has no `AudioContext`, no
  `AudioWorklet` and no way to make a sound, so anything that touched them directly would be another
  surface only a human with a microphone could exercise — which is how, in this project's own words,
  "the four unreachable-surface bugs got shipped". The conversions below are pure functions over
  arrays and are tested as such; the browser wiring is one factory at the bottom, marked untested,
  exactly as `browserCapture` is.
*/

/**
 * How much audio goes in one frame.
 *
 * 2,048 samples at 24 kHz is about 85 ms — small enough that an interruption is heard almost at once,
 * large enough that a conversation is not a few hundred websocket frames a second. The realtime
 * surface buffers server-side and detects turns itself, so this number trades latency against frame
 * count and nothing else.
 */
export const FRAME_SAMPLES = 2048;

/**
 * Float samples to signed 16-bit, which is what the wire carries.
 *
 * CLAMPED, NOT WRAPPED. `Math.round(1.2 * 32767)` overflows a signed short, and a wrap turns a loud
 * moment into the opposite loud moment — a click at best and a scream at worst. The Web Audio API
 * does not promise the [-1, 1] range: a gain node, an aggressive microphone or simple summing can
 * exceed it, so the clamp is not defensive dressing.
 *
 * The asymmetric scale is deliberate too. Signed 16-bit runs -32768..32767, so scaling both
 * directions by 32767 gives up one value at the bottom rather than letting -1.0 land on -32768 and
 * round-trip differently from +1.0.
 */
export function pcm16FromFloat32(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i]!;
    const clamped = s > 1 ? 1 : s < -1 ? -1 : s;
    out[i] = Math.round(clamped * 32767);
  }
  return out;
}

/** And back, for playing what the model said. */
export function float32FromPcm16(pcm: Int16Array): Float32Array {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i += 1) out[i] = pcm[i]! / 32767;
  return out;
}

/**
 * PCM16 to base64, little-endian, because that is what the protocol says and what x86 and ARM both
 * are — but stated rather than assumed, since `Int16Array`'s own byte order is the platform's and a
 * big-endian machine would silently transmit noise.
 */
export function base64FromPcm16(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.length * 2);
  for (let i = 0; i < pcm.length; i += 1) {
    const v = pcm[i]!;
    bytes[i * 2] = v & 0xff;
    bytes[i * 2 + 1] = (v >> 8) & 0xff;
  }
  let binary = '';
  // Chunked, because `String.fromCharCode(...bytes)` on a long frame overflows the argument list —
  // a failure that appears only once somebody speaks for long enough, which is the worst time.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * The inverse, for a chunk of the model's speech.
 *
 * NO SIGN EXTENSION, and that is a correction rather than an omission. This was written as
 * `(lo | hi << 8) << 16 >> 16`, which reads like it is doing something — and a mutation removing the
 * shifts changed no result, because assigning to an `Int16Array` element already truncates to a
 * signed short. The shifts were doing nothing except suggesting to a reader that they were load
 * bearing. The round-trip test carries the negatives that prove the remaining line is enough.
 */
export function pcm16FromBase64(base64: string): Int16Array {
  const binary = atob(base64);
  const out = new Int16Array(binary.length >> 1);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8);
  }
  return out;
}

/**
 * Drop a stream from whatever rate the browser gave us to the one the model wants.
 *
 * `new AudioContext({ sampleRate: 24000 })` is honoured by current browsers and this should almost
 * never run — but "almost never" is not "never": a context can come back at the hardware's rate, and
 * sending 48 kHz samples labelled as 24 kHz makes everybody sound like they are talking at double
 * speed, which is a bug report nobody would connect to a sample rate.
 *
 * NEAREST-NEIGHBOUR, AND THAT IS A REAL LIMIT RATHER THAN A SHORTCUT. Dropping every other sample
 * without a low-pass filter aliases anything above 12 kHz back down into the band. Speech has little
 * energy up there and a transcription model is robust to it, so the trade is acceptable for a
 * fallback that should not fire — but it would not be acceptable as the main path, and if it ever
 * becomes one this comment is the reason to revisit it rather than to trust it.
 */
export function downsample(samples: Float32Array, from: number, to: number): Float32Array {
  if (from <= to) return samples;
  const ratio = from / to;
  const out = new Float32Array(Math.floor(samples.length / ratio));
  for (let i = 0; i < out.length; i += 1) out[i] = samples[Math.floor(i * ratio)]!;
  return out;
}

/* ------------------------------------------------------------------ *
 * Playing what comes back
 * ------------------------------------------------------------------ */

/** The slice of `AudioContext` playback needs, narrowed so a test can be one. */
export interface PlaybackContext {
  readonly currentTime: number;
  readonly sampleRate: number;
  createBuffer(channels: number, length: number, sampleRate: number): PlaybackBuffer;
  createSource(buffer: PlaybackBuffer): PlaybackSource;
}

export interface PlaybackBuffer {
  copyToChannel(source: Float32Array, channel: number): void;
}

export interface PlaybackSource {
  start(at: number): void;
  stop(): void;
}

/**
 * The model's speech, played in the order it arrives.
 *
 * A QUEUE RATHER THAN A PLAY-ON-ARRIVAL, because the chunks come faster than real time. Scheduling
 * each one at "now" would play them all at once, on top of each other; scheduling each at the end of
 * the last is what makes it a voice.
 *
 * `max(currentTime, nextStart)` rather than `nextStart` alone: if the network stalls long enough for
 * the queue to drain, `nextStart` is in the past, and a source scheduled in the past plays
 * immediately anyway — but the cursor would stay behind and every later chunk would pile up against
 * it. Taking the later of the two lets the queue recover instead of collapsing.
 *
 * `clear()` IS BARGE-IN, and it is why the sources are held. Everything already scheduled has to be
 * stopped, not merely un-queued: the whole point of interrupting is that the answer stops NOW, and
 * audio handed to the output has already left this code's control.
 */
export class PlaybackQueue {
  private nextStart = 0;
  private readonly playing = new Set<PlaybackSource>();

  constructor(private readonly context: PlaybackContext) {}

  play(pcm: Int16Array): void {
    if (pcm.length === 0) return;
    const buffer = this.context.createBuffer(1, pcm.length, REALTIME_SAMPLE_RATE);
    buffer.copyToChannel(float32FromPcm16(pcm), 0);
    const source = this.context.createSource(buffer);
    const at = Math.max(this.context.currentTime, this.nextStart);
    source.start(at);
    this.nextStart = at + pcm.length / REALTIME_SAMPLE_RATE;
    this.playing.add(source);
  }

  /** Stop everything, now. The person has started talking. */
  clear(): void {
    for (const source of this.playing) {
      try {
        source.stop();
      } catch {
        // Already finished. A source that has ended throws on `stop`, and one that has is one less
        // thing to stop — swallowing it here is not hiding a failure.
      }
    }
    this.playing.clear();
    this.nextStart = 0;
  }

  /** How much audio is still scheduled, in seconds. For a caller that wants to know if it is talking. */
  get pendingSeconds(): number {
    return Math.max(0, this.nextStart - this.context.currentTime);
  }
}

/* ------------------------------------------------------------------ *
 * The browser
 * ------------------------------------------------------------------ */

/** A live microphone, delivering frames until it is stopped. */
export interface AudioSource {
  stop(): void;
}

/** Everything the browser has to provide, so a test can provide it instead. */
export interface RealtimeAudioEnvironment {
  openSource(onFrame: (base64: string) => void): Promise<AudioSource>;
  openPlayback(): PlaybackContext & { close(): void };
}

/**
 * The worklet, as source, because it has to be a separate module and does not have to be a file.
 *
 * `AudioWorkletProcessor` runs on the audio thread and can only be loaded from a URL, so the usual
 * shape is a second build artefact. A blob URL avoids that entirely: no Vite asset handling, no
 * second file to keep in step with this one, and the code is readable in the place that uses it.
 *
 * THE CSP CONSEQUENCE, STATED BECAUSE IT IS EASY TO MISS. The SPA has no Content-Security-Policy
 * today, so nothing blocks this — but `PLATFORM-PLAN.md` commits to one, and a blob-loaded worklet
 * needs `worker-src blob:` (or `script-src blob:`) when that arrives. That is a smaller and more
 * defensible allowance than the alternative this replaces, and it is written down here so whoever
 * writes that policy finds the requirement rather than the failure.
 *
 * It copies rather than transfers, because the input buffer is reused by the audio thread between
 * calls — a transferred view would be neutered under the next render quantum, which is the kind of
 * fault that appears as intermittent silence.
 */
const WORKLET_SOURCE = `
class PcmFrames extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Float32Array(${FRAME_SAMPLES});
    this.filled = 0;
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;
    for (let i = 0; i < channel.length; i += 1) {
      this.buffer[this.filled++] = channel[i];
      if (this.filled === this.buffer.length) {
        this.port.postMessage(this.buffer.slice(0));
        this.filled = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-frames', PcmFrames);
`;

/** The real browser, wired to the interfaces above. Untested, for the reason the header gives. */
export function browserRealtimeAudio(): RealtimeAudioEnvironment {
  return {
    async openSource(onFrame) {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const context = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
      const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
      try {
        await context.audioWorklet.addModule(url);
      } finally {
        // The module is compiled by now; holding the URL would leak it for the life of the document.
        URL.revokeObjectURL(url);
      }
      const node = new AudioWorkletNode(context, 'pcm-frames');
      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        const samples = downsample(event.data, context.sampleRate, REALTIME_SAMPLE_RATE);
        onFrame(base64FromPcm16(pcm16FromFloat32(samples)));
      };
      context.createMediaStreamSource(stream).connect(node);
      return {
        stop() {
          node.port.onmessage = null;
          node.disconnect();
          // EVERY TRACK, on every exit path — the same rule `VoiceCapture.release` states. A stream
          // left running keeps the browser's recording indicator lit after the feature is visibly
          // over, which people reasonably read as the application still listening to them.
          for (const track of stream.getTracks()) track.stop();
          void context.close();
        },
      };
    },

    openPlayback() {
      const context = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
      return {
        get currentTime() {
          return context.currentTime;
        },
        get sampleRate() {
          return context.sampleRate;
        },
        createBuffer: (channels, length, rate) => context.createBuffer(channels, length, rate),
        createSource(buffer) {
          const source = context.createBufferSource();
          source.buffer = buffer as AudioBuffer;
          source.connect(context.destination);
          return source;
        },
        close: () => void context.close(),
      };
    },
  };
}
